<template>
  <div>
    <v-container fluid pa-0>
      <v-row>
        <!-- E-mail and password -->
        <v-col cols="12">
          <v-card>
            <v-card-title>
              <helpers-section-sub-header>Profile</helpers-section-sub-header>
            </v-card-title>
            <v-card-text>
              <v-form v-model="validEmail" @submit.prevent>
                <v-text-field
                  v-model="userLocal.email"
                  :rules="[rules.required, rules.email]"
                  label="E-mail"
                  autocorrect="off"
                  spellcheck="false"
                  style="max-width: 350px"
                />
              </v-form>
              <v-btn small class="mx-0 mt-4" @click="changePasswordDialog = true">
                Change password
              </v-btn>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 justify-end">
              <v-btn
                :loading="saving.email"
                :disabled="!validEmail || saving.email || user.email === userLocal.email"
                color="primary"
                @click="saveEmail()"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Social visibility -->
        <v-col cols="12">
          <v-card>
            <v-card-title class="d-block">
              <helpers-section-sub-header class="mb-2">
                Social visibility
              </helpers-section-sub-header>
              
            </v-card-title>
            <v-card-text>
              <div >
                We're gradually integrating new features to make WhaleWisdom more social. Use the
                <a href="/whale_watching" target="_blank">Whale Watching site</a>
                to participate in discussions on funds or stock picks, share interesting backtesting
                strategies and more. Making yourself visible allows other users to message you and
                see who you are following
              </div>
              <v-select
                v-model="userLocal.social_visibility_id"
                :items="socialVisibilityItems"
                item-title="text"
                label="Do you want to participate?"
                style="max-width: 350px"
              />

              <!-- Display name -->
              <v-text-field
                v-model="userLocal.name"
                :error="Boolean(displayNameError)"
                :error-messages="displayNameError || null"
                label="Display name"
                autocorrect="off"
                spellcheck="false"
                hint="* Required to post on the site"
                persistent-hint
                class="mb-2"
                style="max-width: 350px"
              >
                <template v-if="user.name !== userLocal.name" #append>
                  <v-row justify="center" align="center" pa-2 style="height: 40px">
                    <v-col pa-0>
                      <v-progress-circular
                        v-if="checkingNameAvailability"
                        :size="16"
                        :width="2"
                        indeterminate
                      />
                      <v-icon v-else :color="displayNameError ? 'error' : 'success'">
                        {{ displayNameError ? mdiAlertCircleOutline : mdiCheckCircleOutline }}
                      </v-icon>
                    </v-col>
                  </v-row>
                </template>
              </v-text-field>

              <!-- Avatar -->
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="uploadAvatar"
              />

              <v-list-subheader class="pa-0"> Avatar </v-list-subheader>
              <div class="avatar-container">
                <v-avatar size="60px" class="mr-6">
                  <img :src="avatarImageSrc" alt="Avatar" @error="userLocal.avatar_url = ''" />
                </v-avatar>
                <v-btn
                  :loading="saving.avatar"
                  :disabled="saving.avatar"
                  small
                  class="my-0 mr-1"
                  @click="$refs.fileInput.click()"
                >
                  Upload
                </v-btn>
                <v-btn
                  v-if="userLocal.avatar_url"
                  :loading="removingAvatar"
                  :disabled="removingAvatar"
                  small
                  text
                  color="error"
                  class="ma-0"
                  @click="removeAvatar()"
                >
                  Remove
                </v-btn>
              </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 justify-end">
              <v-btn
                :loading="saving.socials"
                :disabled="
                  saving.socials || !socialsChanged || checkingNameAvailability || displayNameError
                "
                color="primary"
                @click="saveSocials()"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog: Change password -->
    <v-dialog v-model="changePasswordDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <v-toolbar-title>Change Password</v-toolbar-title>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="password.password"
            :append-icon="password.show ? mdiEyeCheck : mdiEye"
            :type="password.show ? 'text' : 'password'"
            :error="Boolean(password.error)"
            placeholder="New Password"
            counter="25"
            autofocus
            hint="Between 6-25 characters"
            @click:append="password.show = !password.show"
            @input="password.error = null"
            @keyup.enter="savePassword()"
          />
          <v-text-field
            v-model="password.password_confirm"
            :append-icon="password.show ? mdiEyeCheck : mdiEye"
            :type="password.show ? 'text' : 'password'"
            :error="Boolean(password.error)"
            placeholder="Confirm New Password"
            @click:append="password.show = !password.show"
            @input="password.error = null"
            @keyup.enter="savePassword()"
          />
          <div v-show="password.error" class="v-messages text-error">
            {{ password.error }}
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn :disabled="saving.password" depressed @click="cancelPasswordChange()">
            Cancel
          </v-btn>
          <v-btn
            :loading="saving.password"
            :disabled="saving.password"
            depressed
            color="primary"
            @click="savePassword()"
          >
            Change password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapStoreState } from '@/store/helpers'
import debounce from 'lodash/debounce'

// Images
import profilePlaceholder from '@/assets/images/profile-pic-placeholder.png'
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiEye,
  mdiEyeCheck,
  mdiEyeClosed
} from '@mdi/js'

export default {
  data() {
    return {
      mdiAlertCircleOutline,
      mdiCheckCircleOutline,
      mdiEye,
      mdiEyeClosed,
      mdiEyeCheck,
      changePasswordDialog: false,
      userLocal: {
        email: '',
        social_visibility_id: null,
        name: '',
        avatar_url: ''
      },
      validEmail: false,
      checkingNameAvailability: false,
      displayNameError: null,
      saving: {
        email: false,
        password: false,
        socials: false,
        avatar: false
      },
      removingAvatar: false,
      avatarRemoved: false,
      password: {
        password: '',
        password_confirm: '',
        show: false,
        error: null
      },
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail'
        }
      },
      socialVisibilityItems: [
        { value: 0, text: 'No, keep me invisible' },
        { value: 1, text: 'Yes, make me visible' }
      ]
    }
  },

  computed: {
    ...mapStoreState({
      user: state => state.user.user
    }),

    avatarImageSrc() {
      return this.userLocal.avatar_url || profilePlaceholder
    },

    socialsChanged() {
      return (
        this.user.social_visibility_id !== this.userLocal.social_visibility_id ||
        this.user.name !== this.userLocal.name
      )
    }
  },

  watch: {
    'userLocal.name'(val) {
      if (!val || val.length < 6) {
        this.displayNameError = 'Display name must be at least 6 characters'
        return
      }

      this.checkingNameAvailability = true
      this.checkNameAvailability(val)
    }
  },

  created() {
    this.syncLocalUserProxy()
  },

  methods: {
    saveEmail() {
      this.saving.email = true

      api
        .post('user.json', {
          ...this.user,
          email: this.userLocal.email
        })
        .then(() => {
          useUserStore()
            .loadUser()
            .then(() => this.syncLocalUserProxy())
          useAppStore().showNotification({ type: 'success', text: 'E-mail changed successfully' })
        })
        .catch(error =>
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        )
        .then(() => (this.saving.email = false))
    },

    validatePassword() {
      if (this.password.password.length < 6) {
        this.password.error = 'Min 6 characters'
        return
      }
      if (this.password.password.length > 25) {
        this.password.error = 'Max 25 characters'
        return
      }
      if (this.password.password !== this.password.password_confirm) {
        this.password.error = 'Passwords do not match'
        return
      }

      this.password.error = null
    },

    cancelPasswordChange() {
      this.changePasswordDialog = false
      this.password = {
        password: '',
        password_confirm: '',
        show: false,
        error: null
      }
    },

    savePassword() {
      this.saving.password = true
      this.validatePassword()

      if (this.password.error) return

      api
        .post('password.json', this.password)
        .then(() => {
          useUserStore().loadUser()
          this.changePasswordDialog = false
          useAppStore().showNotification({ type: 'success', text: 'Password changed successfully' })
        })
        .catch(error => console.log(error))
        .then(() => (this.saving.password = false))
    },

    saveSocials() {
      this.saving.socials = true

      api
        .post('user.json', {
          ...this.user,
          name: this.userLocal.name,
          social_visibility_id: this.userLocal.social_visibility_id
        })
        .then(() => {
          useUserStore()
            .loadUser()
            .then(() => this.syncLocalUserProxy())
          useAppStore().showNotification({
            type: 'success',
            text: 'Social visibility settings updated successfully'
          })
        })
        .catch(error => console.log(error))
        .then(() => (this.saving.socials = false))
    },

    checkNameAvailability: debounce(function () {
      this.checkingNameAvailability = true

      api
        .get('user_name_available', { params: { name: this.userLocal.name } })
        .then(response => {
          this.displayNameError = response.data === 'Available' ? null : 'Name already in use'
        })
        .catch(error => (this.displayNameError = error))
        .then(() => (this.checkingNameAvailability = false))
    }, 750),

    uploadAvatar(e) {
      if (e && e.target.files && e.target.files[0]) {
        const img = e.target.files[0]
        const formData = new FormData()
        formData.append('file', img)

        this.saving.avatar = true

        api
          .post('user_avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
          .then(() =>
            useUserStore()
              .loadUser()
              .then(() => this.syncLocalUserProxy())
          )
          .catch(error => console.log(error))
          .then(() => (this.saving.avatar = false))
      }
    },

    removeAvatar() {
      this.removingAvatar = true

      api
        .delete('user_avatar')
        .then(() => useUserStore().loadUser())
        .catch(error => console.log(error))
        .then(() => (this.removingAvatar = false))
    },

    // Keep track of the changes made so that we can
    // enable the 'Save' buttons only if needed
    syncLocalUserProxy() {
      this.userLocal = {
        email: this.user.email,
        social_visibility_id: this.user.social_visibility_id,
        name: this.user.name,
        avatar_url: this.user.avatar_url
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.avatar-container {
  display: flex;
  align-items: center;
}
</style>
